import { Link } from "react-router-dom";
import type { Case } from "../../types/caseView";

// ── Status config ──────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; glow: string }> = {
  draft:        { label: "Draft",        bg: "bg-slate-100",   text: "text-slate-600",   glow: "none" },
  letter_ready: { label: "Letter Ready", bg: "bg-teal-50",     text: "text-teal-700",    glow: "inset 3px 0 8px -2px rgba(20, 184, 166, 0.3)" },
  submitted:    { label: "Submitted",    bg: "bg-blue-50",     text: "text-blue-700",    glow: "inset 3px 0 8px -2px rgba(59, 130, 246, 0.3)" },
  denied:       { label: "Denied",       bg: "bg-amber-50",    text: "text-amber-700",   glow: "inset 3px 0 8px -2px rgba(245, 158, 11, 0.4)" },
  appealed:     { label: "Appealed",     bg: "bg-amber-50",    text: "text-amber-700",   glow: "inset 3px 0 8px -2px rgba(245, 158, 11, 0.4)" },
  approved:     { label: "Approved",     bg: "bg-emerald-50",  text: "text-emerald-700", glow: "inset 3px 0 8px -2px rgba(16, 185, 129, 0.3)" },
};

interface StatusAction {
  label: string;
  style: string;
  onClick: () => void;
}

function getStatusAction(
  status: string,
  callbacks: {
    onStatusChange?: (status: string) => void;
    onDenialCapture?: () => void;
    onGenerateAppeal?: () => void;
  }
): StatusAction | null {
  switch (status) {
    case "draft":
    case "letter_ready":
      return callbacks.onStatusChange
        ? { label: "Mark Submitted", style: "bg-teal-500 hover:bg-teal-600 text-white", onClick: () => callbacks.onStatusChange?.("submitted") }
        : null;
    case "submitted":
      return callbacks.onDenialCapture
        ? { label: "Record Denial", style: "bg-amber-500 hover:bg-amber-600 text-white", onClick: callbacks.onDenialCapture }
        : null;
    case "denied":
      return callbacks.onGenerateAppeal
        ? { label: "Start Appeal", style: "bg-amber-500 hover:bg-amber-600 text-white", onClick: callbacks.onGenerateAppeal }
        : null;
    default:
      return null;
  }
}

// ── Icons ──────────────────────────────────────────────────

function IconStar({ filled, className }: { filled: boolean; className?: string }) {
  return filled ? (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
    </svg>
  ) : (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  );
}

function IconPencil({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    </svg>
  );
}

function IconWarningTriangle({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

// ── Component ──────────────────────────────────────────────

interface CaseSummaryCardProps {
  caseData: Case;
  onStar: () => void;
  onStatusChange?: (status: string) => void;
  onDenialCapture?: () => void;
  onGenerateAppeal?: () => void;
}

export function CaseSummaryCard({ caseData, onStar, onStatusChange, onDenialCapture, onGenerateAppeal }: CaseSummaryCardProps) {
  const isDenied = caseData.case_status === "denied" || caseData.case_status === "appealed";
  const status = STATUS_CONFIG[caseData.case_status || "draft"] || STATUS_CONFIG.draft;
  const statusAction = getStatusAction(caseData.case_status || "draft", { onStatusChange, onDenialCapture, onGenerateAppeal });

  // Build patient subtitle from age/sex if available
  const patientInfo = [caseData.age_range, caseData.sex].filter(Boolean).join(", ");

  // Strip redundant prefixes from request_details (label already says "Study")
  const cleanRequestDetails = caseData.request_details
    ?.replace(/^(Study|Procedure|Service|Request):\s*/i, "")
    ?.trim();

  // Only show fields that have data
  const details: { label: string; value: string }[] = [
    cleanRequestDetails ? { label: "Study", value: cleanRequestDetails } : null,
    caseData.diagnoses ? { label: "Dx", value: caseData.diagnoses } : null,
    caseData.tried_already ? { label: "Prior Tx", value: caseData.tried_already } : null,
    isDenied && caseData.plan_type ? { label: "Plan", value: caseData.plan_type } : null,
  ].filter((d): d is { label: string; value: string } => d !== null);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      style={{ boxShadow: `${status.glow}, 0 1px 3px 0 rgba(0,0,0,0.05)` }}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-bold text-slate-900 truncate capitalize">
                {caseData.friendly_name}
              </h1>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${status.bg} ${status.text} flex-shrink-0`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {caseData.insurance_company && (
                <span className="text-sm text-slate-600 font-medium">
                  {caseData.insurance_company}
                </span>
              )}
              {caseData.insurance_company && caseData.request_type && (
                <span className="text-slate-300">·</span>
              )}
              {caseData.request_type && (
                <span className="text-sm text-slate-500">{caseData.request_type}</span>
              )}
              {patientInfo && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-sm text-slate-500">{patientInfo}</span>
                </>
              )}
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-400">
                {new Date(caseData.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-0.5 flex-shrink-0 ml-3">
            {statusAction && (
              <button
                onClick={statusAction.onClick}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors mr-1 ${statusAction.style}`}
              >
                {statusAction.label}
              </button>
            )}
            <button
              onClick={onStar}
              className="p-1.5 rounded-md hover:bg-slate-50 transition-colors"
              title={caseData.is_starred ? "Unstar case" : "Star case"}
              aria-label={caseData.is_starred ? "Unstar case" : "Star case"}
              aria-pressed={!!caseData.is_starred}
            >
              <IconStar
                filled={!!caseData.is_starred}
                className={`w-4 h-4 ${caseData.is_starred ? "text-amber-500" : "text-slate-300"}`}
              />
            </button>
            <Link
              to={`/new?edit=${caseData.id}`}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
              title="Edit case"
            >
              <IconPencil className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Detail rows — only populated fields */}
      {details.length > 0 && (
        <div className="px-5 pb-4 pt-2 border-t border-slate-50">
          <div className="space-y-2">
            {details.map((d, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide w-16 flex-shrink-0 pt-0.5">
                  {d.label}
                </span>
                <span className="text-sm text-slate-700 leading-snug">
                  {d.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Denial reason — amber callout, only when relevant */}
      {isDenied && caseData.denial_reason && (
        <div className="px-5 pb-4">
          <div className="px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-200/60 flex gap-2.5">
            <IconWarningTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-0.5">
                Denial Reason
              </p>
              <p className="text-sm text-amber-900 leading-relaxed">
                {caseData.denial_reason}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}